import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Star, MapPin, Calendar, Globe, ExternalLink, Users, Clock, Camera, Loader2, CheckCircle, XCircle, AlertTriangle, X, User, ImagePlus, Images } from 'lucide-react';
import axios from '../utils/api';
import { API } from '../utils/api';
import { useCommunityAuth } from '../context/CommunityAuthContext';
import { getImageForChallenge, getCategory, categoryStyles } from '../utils/homeHelpers';
import notify from '../utils/notify';
import { eventAttendeeCount, challengeParticipantCount, eventSeatsLeft } from '../utils/communityMembership';
import GroupRegisterSheet from '../components/community/GroupRegisterSheet';

const TEAL = '#5DBFAD';

// Session-scoped in-memory cache so we don't re-translate the same event repeatedly
// while navigating around the app. Keyed by event id.
const eventTranslationCache = {};

// Static UI strings (section labels, stats labels, button states) translated to Hindi.
// These are NOT DB content — they're hardcoded UI chrome that should follow the lang toggle.
const UI_HI = {
  'sections.about': '\u092A\u0930\u093F\u091A\u092F',                        // परिचय
  'sections.how': '\u0915\u0948\u0938\u0947 \u092A\u0942\u0930\u093E \u0915\u0930\u0947\u0902',  // कैसे पूरा करें
  'sections.location': '\u0938\u094D\u0925\u093E\u0928',                     // स्थान
  'sections.deadline': '\u0938\u092E\u092F \u0938\u0940\u092E\u093E',         // समय सीमा
  'sections.impact': '\u0906\u092A\u0915\u093E \u092A\u094D\u0930\u092D\u093E\u0935',  // आपका प्रभाव
  'sections.organiser': '\u0906\u092F\u094B\u091C\u0915',                    // आयोजक
  'sections.memories': '\u0915\u093E\u0930\u094D\u092F\u0915\u094D\u0930\u092E \u092F\u093E\u0926\u0947\u0902',  // कार्यक्रम यादें
  'stats.joined': '\u0936\u093E\u092E\u093F\u0932',                          // शामिल
  'stats.frequency': '\u0906\u0935\u0943\u0924\u094D\u0924\u093F',           // आवृत्ति
  'stats.stars': '\u0938\u093F\u0924\u093E\u0930\u0947',                     // सितारे
  'verify.title': '\u0909\u092A\u0938\u094D\u0925\u093F\u0924\u093F \u0938\u0924\u094D\u092F\u093E\u092A\u093F\u0924 \u0915\u0930\u0947\u0902',  // उपस्थिति सत्यापित करें
  'verify.submitting': '\u092D\u0947\u091C \u0930\u0939\u0947 \u0939\u0948\u0902...',  // भेज रहे हैं...
  'verify.photoTitle': '\u092B\u094B\u091F\u094B \u0905\u092A\u0932\u094B\u0921 \u0915\u0930\u0947\u0902',  // फोटो अपलोड करें
  'verify.photoSub': '\u0938\u092C\u0942\u0924 \u0915\u0947 \u0930\u0942\u092A \u092E\u0947\u0902 \u090F\u0915 \u092B\u094B\u091F\u094B \u0932\u0947\u0902 \u092F\u093E \u0905\u092A\u0932\u094B\u0921 \u0915\u0930\u0947\u0902',
  'verify.locTitle': '\u0938\u094D\u0925\u093E\u0928 \u0938\u093E\u091D\u093E \u0915\u0930\u0947\u0902',  // स्थान साझा करें
  'verify.locSub': '\u092A\u0941\u0937\u094D\u091F\u093F \u0915\u0930\u0947\u0902 \u0915\u093F \u0906\u092A \u0915\u093E\u0930\u094D\u092F\u0915\u094D\u0930\u092E \u0938\u094D\u0925\u0932 \u092A\u0930 \u0939\u0948\u0902',
  // Category pills — finite enum from homeHelpers.getCategory()
  'pillar.Wellness':   '\u0938\u094D\u0935\u093E\u0938\u094D\u0925\u094D\u092F',                       // स्वास्थ्य
  'pillar.Inclusion':  '\u0938\u092E\u093E\u0935\u0947\u0936',                                       // समावेश
  'pillar.Career Dev': '\u0915\u0930\u093F\u092F\u0930 \u0935\u093F\u0915\u093E\u0938',              // करियर विकास
  'pillar.Community':  '\u0938\u092E\u0941\u0926\u093E\u092F',                                       // समुदाय
  'pillar.Climate':    '\u091C\u0932\u0935\u093E\u092F\u0941',                                       // जलवायु
};

export default function CommunityEventDetailPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user, token, refreshUser } = useCommunityAuth();
  const event = state?.event;
  const eventId = event?.eventId || event?.id;
  const [lang, setLang] = useState('en');
  const [joining, setJoining] = useState(false);
  const [myRecord, setMyRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(true);
  const [showVerifySheet, setShowVerifySheet] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [memory, setMemory] = useState(null);
  const [uploadingMemory, setUploadingMemory] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [liveEvent, setLiveEvent] = useState(null);
  const [showGroupSheet, setShowGroupSheet] = useState(false);
  const isOrganiser = user?.role === 'COMMUNITY_ORGANISER';

  // Translation state — fetched from Grok via backend, then cached.
  // We only treat a payload as "translated" if it's not a fallback response. Fallbacks
  // (Grok unavailable) intentionally don't poison the cache so the next toggle retries.
  const cachedHi = (eventId && eventTranslationCache[eventId]) || event?.translations?.hi || null;
  const [translatedContent, setTranslatedContent] = useState(
    cachedHi && !cachedHi.fallback ? cachedHi : null
  );
  const [isTranslating, setIsTranslating] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  // This page is the EVENT detail page — it's only ever reached for events.
  // The previous `event?.type === 'event'` check was always false because
  // backend documents carry `event_type` (e.g. "in-person"), not `type`. That
  // bug caused the attendance fetch to be skipped and the Join button to land
  // in the wrong branch, so clicking it appeared to do nothing.
  const isEvent = true;
  const isAdmin = user?.role === 'COMMUNITY_ADMIN';

  // `t()` resolves a Hindi value by precedence: DB-translated content → static UI map → English fallback.
  const t = useCallback((enVal, hiKey) => {
    if (lang !== 'hi') return enVal;
    if (translatedContent && translatedContent[hiKey]) return translatedContent[hiKey];
    if (UI_HI[hiKey]) return UI_HI[hiKey];
    return enVal;
  }, [lang, translatedContent]);

  // Toggle language. On first switch to Hindi we hit the backend Grok proxy and cache the result.
  // Per-field silent fallback: if a specific field has no translation, `t()` falls through to English.
  // If the whole call fails (network error / Grok unavailable), we silently render English without
  // surfacing a "translation failed" toast to the user.
  const handleToggleLang = async () => {
    if (lang === 'hi') { setLang('en'); return; }
    if (translatedContent && translatedContent.title && !translatedContent.fallback) { setLang('hi'); return; }
    if (!eventId) { setLang('hi'); return; }
    setIsTranslating(true);
    try {
      const res = await axios.post(`${API}/community/events/${eventId}/translate`, {}, authHeaders);
      const data = res.data || {};
      if (data.fallback) {
        // Don't cache fallback — allow retry on next toggle. Silently show English.
        setTranslatedContent(null);
      } else {
        eventTranslationCache[eventId] = data;
        setTranslatedContent(data);
      }
      setLang('hi');
    } catch (err) {
      // Network/server failure — silently keep English content visible.
      setTranslatedContent(null);
      setLang('hi');
    } finally {
      setIsTranslating(false);
    }
  };

  // Fetch attendance record and memory on mount
  useEffect(() => {
    if (!eventId || !isEvent) { setLoadingRecord(false); return; }
    (async () => {
      try {
        const [attRes, memRes, evRes] = await Promise.all([
          axios.get(`${API}/community/events/${eventId}/my-attendance`, authHeaders),
          axios.get(`${API}/community/events/${eventId}/memory`, authHeaders).catch(() => ({ data: { memory: null } })),
          axios.get(`${API}/community/events/${eventId}`, authHeaders).catch(() => ({ data: null })),
        ]);
        setMyRecord(attRes.data.record || null);
        setLiveEvent(evRes.data || null);
        setMemory(memRes.data.memory || null);
      } catch (e) { console.error('[community/events/my-attendance] fetch failed', e); }
      finally { setLoadingRecord(false); }
    })();
  }, [eventId]);

  const refreshLiveEvent = async () => {
    try { const r = await axios.get(`${API}/community/events/${eventId}`, authHeaders); setLiveEvent(r.data); } catch {}
  };

  const handleJoin = async (body) => {
    if (joining || myRecord) return;
    if (isOrganiser && !body) { setShowGroupSheet(true); return; }
    if (!token) {
      notify.error('Please sign in again to join this event.');
      return;
    }
    if (!eventId) {
      notify.error('Could not identify the event. Please open it from the events list again.');
      return;
    }
    setJoining(true);
    try {
      const res = await axios.post(`${API}/community/events/${eventId}/rsvp`, body || {}, authHeaders);
      setShowGroupSheet(false);
      refreshLiveEvent();
      // Backend returns { message, record } — set state from the canonical
      // server response, never optimistically (so a 4xx never leaves the
      // button stuck in a fake "Joined" state).
      const record = res.data?.record || { status: 'pending' };
      setMyRecord(record);
      notify.success("You're in — verify your attendance on the day of the event.");
      if (typeof refreshUser === 'function') {
        // Fire-and-forget — refreshes the stars badge in the top header but
        // we don't need to block the UI on it.
        refreshUser();
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message || 'Could not join the event. Please try again.';
      console.error('[community/events/rsvp] failed', status, detail);
      if (status === 409) {
        notify.error(detail);
        refreshLiveEvent();
      } else if (status === 400 && /already joined/i.test(detail)) {
        // Already joined — reconcile by fetching the authoritative record.
        try {
          const r2 = await axios.get(`${API}/community/events/${eventId}/my-attendance`, authHeaders);
          setMyRecord(r2.data?.record || { status: 'pending' });
        } catch { setMyRecord({ status: 'pending' }); }
      } else {
        // Surface every other error so the user knows what happened.
        notify.error(detail);
      }
    } finally { setJoining(false); }
  };

  const handleLeaveEvent = async () => {
    if (leaving) return;
    setLeaving(true);
    try {
      const res = await axios.delete(`${API}/community/events/${eventId}/rsvp`, authHeaders);
      const { was_verified: wasVerified, deducted_stars: deductedStars } = res.data || {};
      // Reset the local attendance state so the button reverts to Join and
      // the Leave block hides. Refresh the user so the top-header stars pill
      // reflects the deduction without a page reload.
      setMyRecord(null);
      setShowLeaveConfirm(false);
      if (typeof refreshUser === 'function') refreshUser();
      if (wasVerified && deductedStars) {
        notify.success(`You have left this event. ${deductedStars} stars deducted.`);
      } else {
        notify.success('You have left this event.');
      }
    } catch (err) {
      console.error('[community/events/leave] failed', err);
      notify.error(err?.response?.data?.detail || 'Failed to leave this event.');
    } finally { setLeaving(false); }
  };

  // Time window check for verification
  const checkTimeWindow = () => {
    const now = new Date();
    const eventDateStr = event.event_date || event.start_date;
    if (!eventDateStr) return { allowed: true };
    const eventDate = new Date(eventDateStr);
    const todayStr = now.toISOString().slice(0, 10);
    const eventDayStr = eventDate.toISOString().slice(0, 10);
    if (todayStr !== eventDayStr) {
      return { allowed: false, message: 'Verification is only available on the day of the event' };
    }
    if (event.start_time && event.end_time) {
      const [sh, sm] = event.start_time.split(':').map(Number);
      const [eh, em] = event.end_time.split(':').map(Number);
      const currentMins = now.getHours() * 60 + now.getMinutes();
      const startMins = (sh || 0) * 60 + (sm || 0);
      const endMins = (eh || 0) * 60 + (em || 0);
      if (currentMins < startMins) {
        return { allowed: false, message: `Verification opens at ${event.start_time}` };
      }
      if (currentMins > endMins) {
        return { allowed: false, message: 'Verification window has ended' };
      }
    }
    return { allowed: true };
  };

  const handleOpenVerify = () => {
    setVerifyError('');
    const tw = checkTimeWindow();
    if (!tw.allowed) {
      setVerifyError(tw.message);
      return;
    }
    setShowVerifySheet(true);
  };

  const handleSubmitPhoto = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setVerifying(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await axios.post(`${API}/community/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        const proofUrl = uploadRes.data.url;
        await axios.post(`${API}/community/events/${eventId}/verify-attendance`, { proofUrl, verificationMethod: 'photo' }, authHeaders);
        setMyRecord(prev => ({ ...prev, status: 'pending', proofUrl }));
        setShowVerifySheet(false);
      } catch (err) {
        setVerifyError(err.response?.data?.detail || 'Upload failed');
      } finally { setVerifying(false); }
    };
    input.click();
  };

  const handleSubmitLocation = async () => {
    if (!navigator.geolocation) {
      setVerifyError('Geolocation not supported');
      return;
    }
    setVerifying(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          await axios.post(`${API}/community/events/${eventId}/verify-attendance`, { coordinates: coords, verificationMethod: 'location' }, authHeaders);
          setMyRecord(prev => ({ ...prev, status: 'pending', coordinates: coords }));
          setShowVerifySheet(false);
        } catch (err) {
          setVerifyError(err.response?.data?.detail || 'Location submission failed');
        } finally { setVerifying(false); }
      },
      (err) => {
        setVerifyError(err.code === 1 ? 'Location permission denied' : 'Could not get location');
        setVerifying(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleAddMemoryPhoto = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploadingMemory(true);
      try {
        // Ensure memory exists
        if (!memory) {
          const createRes = await axios.post(`${API}/community/events/${eventId}/memory`, {}, authHeaders);
          setMemory(createRes.data);
        }
        // Upload photo
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await axios.post(`${API}/community/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        const imageUrl = uploadRes.data.url;
        // Add to memory
        await axios.post(`${API}/community/events/${eventId}/memory/image`, { image_url: imageUrl }, authHeaders);
        setMemory(prev => prev ? { ...prev, images: [...(prev.images || []), imageUrl] } : { images: [imageUrl] });
      } catch (err) {
        alert(err.response?.data?.detail || 'Failed to add memory photo');
      } finally { setUploadingMemory(false); }
    };
    input.click();
  };

  if (!event) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>
        Event not found. <button onClick={() => navigate(-1)} style={{ color: TEAL, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Go back</button>
      </div>
    );
  }

  const imageUrl = getImageForChallenge(event);
  const pillar = getCategory(event.title, event.category);
  const pillarStyle = categoryStyles[pillar] || categoryStyles['Wellness'];
  const pillarLight = pillarStyle.bg + '30';
  const participantCount = isEvent ? eventAttendeeCount(event) : challengeParticipantCount(event);
  const frequency = event.frequency || 'Weekly';
  const stars = event.reward_points || event.reward_stars || 30;
  const isEnded = event.end_date && new Date(event.end_date) < new Date();
  const coords = event.coordinates || event.location_coords;
  const seatsLeft = eventSeatsLeft(liveEvent || event);
  const isFull = seatsLeft === 0;

  // Determine button state based on attendance record
  const recStatus = myRecord?.status;
  const hasProof = !!(myRecord?.proofUrl || myRecord?.coordinates);

  const getButtonConfig = () => {
    if (isEnded) return { text: lang === 'hi' ? '\u0938\u092E\u093E\u092A\u094D\u0924' : 'Ended', bg: '#E5E7EB', color: '#9CA3AF', disabled: true };
    if (loadingRecord) return { text: '...', bg: '#E5E7EB', color: '#9CA3AF', disabled: true };
    if (!myRecord && isFull) return { text: 'Full', bg: '#E5E7EB', color: '#9CA3AF', disabled: true };
    if (!myRecord) return { text: lang === 'hi' ? '\u0936\u093E\u092E\u093F\u0932 \u0939\u094B\u0902' : 'Join', bg: TEAL, color: '#FFFFFF', disabled: false, action: handleJoin };
    if (recStatus === 'approved') return { text: lang === 'hi' ? '\u0909\u092A\u0938\u094D\u0925\u093F\u0924 \u2713' : 'Attended \u2713', bg: '#D1FAE5', color: '#047857', disabled: true };
    if (recStatus === 'pending' && hasProof) return { text: 'Verification Pending', bg: '#E5E7EB', color: '#6B7280', disabled: true };
    if (recStatus === 'pending' && !hasProof) {
      // The user has RSVP'd but hasn't verified yet. Show a "Joined" state up
      // to the day of the event; once the day arrives, prompt them to verify.
      const today = new Date();
      const evDateStr = event.event_date || event.start_date;
      const isEventDay = evDateStr && new Date(evDateStr).toISOString().slice(0, 10) === today.toISOString().slice(0, 10);
      if (isEventDay) {
        return { text: 'Verify Attendance', bg: TEAL, color: '#FFFFFF', disabled: false, action: handleOpenVerify };
      }
      return { text: lang === 'hi' ? '\u0936\u093E\u092E\u093F\u0932 \u2713' : 'Joined \u2713', bg: '#D1FAE5', color: '#047857', disabled: true };
    }
    if (recStatus === 'rejected') return { text: 'Verification Failed \u2014 Try Again', bg: '#FFFFFF', color: '#E8795E', disabled: false, action: handleOpenVerify, outlined: true };
    return { text: 'Join', bg: TEAL, color: '#FFFFFF', disabled: false, action: handleJoin };
  };

  const btn = getButtonConfig();

  // Verification method for this event
  const vm = event.verification_method || 'manual';
  const showPhoto = vm === 'photo' || vm === 'manual';
  const showLocation = vm === 'location' || event.geo_tag_required || vm === 'manual';

  return (
    <div style={{ background: '#f0f0f0', minHeight: '100vh' }}>
    <div data-testid="event-detail-page" style={{ maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#F8FAFB', paddingBottom: 90 }}>
      {/* A. Hero Image */}
      <div style={{ position: 'relative', width: '100%', height: 220 }}>
        <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => { e.target.style.background = '#E5E7EB'; }} />
        <button data-testid="event-detail-back" onClick={() => navigate(-1)}
          style={{ position: 'absolute', top: 16, left: 16, width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
          <ArrowLeft size={18} color="#1A1A2E" />
        </button>
        <button data-testid="event-detail-lang-toggle" onClick={handleToggleLang} disabled={isTranslating}
          style={{ position: 'absolute', top: 16, right: 16, padding: '6px 14px', borderRadius: 18, background: 'rgba(255,255,255,0.9)', border: 'none', cursor: isTranslating ? 'wait' : 'pointer', fontSize: 13, fontWeight: 700, color: '#1A1A2E', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: 6, opacity: isTranslating ? 0.7 : 1 }}>
          {isTranslating ? <Loader2 size={14} className="animate-spin" /> : null}
          {lang === 'en' ? '\u0939\u093F' : 'EN'}
        </button>
      </div>

      {/* B. Content Card */}
      <div style={{ background: '#FFFFFF', borderRadius: '20px 20px 0 0', marginTop: -16, position: 'relative', zIndex: 1, padding: '24px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1A1A2E', margin: 0, lineHeight: 1.3, flex: 1, fontFamily: 'Poppins, sans-serif' }}>
            {t(event.title, 'title')}
          </h1>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', background: '#F1F5F9', padding: '4px 10px', borderRadius: 12, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {t(event.mode || event.type || 'Solo', 'type')}
          </span>
        </div>

        <div style={{ marginBottom: 16 }}>
          <span style={{ fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 12, background: pillarStyle.bg, color: pillarStyle.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t(pillar, `pillar.${pillar}`)}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', background: pillarLight, borderRadius: 12, padding: '14px 0', marginBottom: 24 }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1A2E' }}>{liveEvent?.attendees_count ?? participantCount}</div>
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{t('Joined', 'stats.joined') || 'Joined'}</div>
          </div>
          <div style={{ width: 1, height: 28, background: '#D1D5DB' }} />
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1A2E' }}>{t(frequency, 'frequencyVal') || frequency}</div>
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{t('Frequency', 'stats.frequency') || 'Frequency'}</div>
          </div>
          <div style={{ width: 1, height: 28, background: '#D1D5DB' }} />
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Star size={14} fill="#F28C28" color="#F28C28" />
              <span style={{ fontSize: 16, fontWeight: 700, color: '#1A1A2E' }}>{stars}</span>
            </div>
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{t('Stars', 'stats.stars') || 'Stars'}</div>
          </div>
        </div>

        {/* Leave Event — destructive outlined button, only shown to users who
            have an attendance record. Stays gated through pending/approved
            states so users can always back out. Placed right below the stats
            strip per the design ask. */}
        {myRecord && !isEnded && (
          <button
            data-testid="leave-event-btn"
            onClick={() => setShowLeaveConfirm(true)}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: '#FFFFFF',
              color: '#DC2626',
              border: '1.5px solid #FCA5A5',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: 24,
            }}
          >
            Leave Event
          </button>
        )}

        <Section label={t('ABOUT', 'sections.about') || 'ABOUT'}>
          <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, margin: 0 }}>
            {t(event.description, 'description')}
          </p>
        </Section>

        {event.instructions?.length > 0 && (
          <Section label={t('HOW TO COMPLETE', 'sections.how') || 'HOW TO COMPLETE'}>
            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: '#374151', lineHeight: 1.8 }}>
              {(lang === 'hi' && translatedContent?.instructions?.length ? translatedContent.instructions : event.instructions).map((inst, i) => (
                <li key={i}>{inst}</li>
              ))}
            </ol>
          </Section>
        )}

        {(event.location || event.city) && (
          <Section label={t('LOCATION', 'sections.location') || 'LOCATION'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151' }}>
              <MapPin size={16} color="#6B7280" />
              <span>{t(event.location || event.city, 'location')}</span>
              {event.audienceType === 'community' && coords?.lat && coords?.lng && (
                <a href={`https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`}
                  target="_blank" rel="noopener noreferrer" data-testid="maps-link"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 16, border: `1.5px solid ${TEAL}`, color: TEAL, fontSize: 12, fontWeight: 600, textDecoration: 'none', marginLeft: 4 }}>
                  Maps <ExternalLink size={12} />
                </a>
              )}
            </div>
          </Section>
        )}

        {(event.end_date || event.deadline) && (
          <Section label={t('DEADLINE', 'sections.deadline') || 'DEADLINE'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151' }}>
              <Calendar size={16} color="#6B7280" />
              <span>{t(event.deadline || new Date(event.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }), 'deadline')}</span>
            </div>
          </Section>
        )}

        {event.impact && (
          <Section label={t('YOUR IMPACT', 'sections.impact') || 'YOUR IMPACT'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151' }}>
              <Globe size={16} color="#6B7280" />
              <span>{t(event.impact, 'impact')}</span>
            </div>
          </Section>
        )}

        {event.pocName && (
          <Section label={t('ORGANISER', 'sections.organiser') || 'ORGANISER'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151' }}>
              <User size={16} color="#6B7280" />
              <span>{t(event.pocName, 'pocName')}</span>
            </div>
          </Section>
        )}

        {/* Event Memories */}
        {(memory?.images?.length > 0 || (isAdmin && isEnded)) && (
          <Section label={t('EVENT MEMORIES', 'sections.memories') || 'EVENT MEMORIES'}>
            {memory?.images?.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: isAdmin && isEnded ? 12 : 0 }}>
                {memory.images.map((img, i) => (
                  <div key={i} style={{ borderRadius: 10, overflow: 'hidden', aspectRatio: '1', background: '#F3F4F6' }}>
                    <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                ))}
              </div>
            )}
            {isAdmin && isEnded && (
              <button
                data-testid="add-memory-btn"
                onClick={handleAddMemoryPhoto}
                disabled={uploadingMemory}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  width: '100%', padding: '12px 16px', borderRadius: 10,
                  border: `1.5px dashed ${TEAL}`, background: '#f0faf9',
                  color: TEAL, fontSize: 14, fontWeight: 600,
                  cursor: uploadingMemory ? 'not-allowed' : 'pointer',
                  opacity: uploadingMemory ? 0.6 : 1,
                }}
              >
                {uploadingMemory ? <><Loader2 size={16} className="animate-spin" /> Uploading...</> : <><ImagePlus size={16} /> Add Memory Photo</>}
              </button>
            )}
          </Section>
        )}

        {/* Verification status message */}
        {verifyError && !showVerifySheet && (
          <div data-testid="verify-error" style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10,
            background: '#FEF2F2', border: '1px solid #FECACA', marginBottom: 16, fontSize: 13, color: '#B91C1C',
          }}>
            <AlertTriangle size={16} /> {verifyError}
          </div>
        )}
      </div>

      {showGroupSheet && (
        <GroupRegisterSheet seatsLeft={seatsLeft} busy={joining}
          onCancel={() => setShowGroupSheet(false)} onSubmit={(body) => handleJoin(body)} />
      )}

      {/* C. Fixed bottom bar */}
      <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', maxWidth: 480, width: '100%', background: '#FFFFFF', borderTop: '1px solid #F3F4F6', padding: '12px 20px 24px', zIndex: 100 }}>
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          {seatsLeft != null && !myRecord && (
            <div data-testid="event-detail-seats-left" style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: isFull ? '#B91C1C' : '#6B7280', marginBottom: 8 }}>
              {isFull ? 'Full — no seats left' : `${seatsLeft} seat${seatsLeft === 1 ? '' : 's'} left`}
            </div>
          )}
          <motion.button
            data-testid="event-detail-join-btn"
            whileTap={!btn.disabled ? { scale: 0.97 } : {}}
            onClick={btn.action ? () => btn.action() : (() => {})}
            disabled={btn.disabled || joining}
            style={{
              width: '100%', padding: '14px 24px', borderRadius: 30,
              border: btn.outlined ? `2px solid ${btn.color}` : 'none',
              fontSize: 16, fontWeight: 700,
              cursor: btn.disabled ? 'default' : 'pointer',
              background: btn.outlined ? '#FFFFFF' : btn.bg,
              color: btn.color,
              boxShadow: (!btn.disabled && !btn.outlined) ? '0 4px 14px rgba(93,191,173,0.3)' : 'none',
            }}>
            {joining ? '...' : btn.text}
          </motion.button>
        </div>
      </div>
    </div>

    {/* Verification Bottom Sheet */}
    <AnimatePresence>
      {showVerifySheet && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200 }}
          onClick={() => !verifying && setShowVerifySheet(false)}
        >
          <motion.div
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
              maxWidth: 480, width: '100%', background: '#FFFFFF',
              borderRadius: '20px 20px 0 0', padding: '24px 20px 40px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1A1A2E', margin: 0 }}>{t('Verify Attendance', 'verify.title')}</h3>
              <button onClick={() => setShowVerifySheet(false)} disabled={verifying}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <X size={20} color="#6B7280" />
              </button>
            </div>

            {verifyError && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10,
                background: '#FEF2F2', border: '1px solid #FECACA', marginBottom: 16, fontSize: 13, color: '#B91C1C',
              }}>
                <AlertTriangle size={16} /> {verifyError}
              </div>
            )}

            {verifying && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '20px 0', color: '#6B7280', fontSize: 14 }}>
                <Loader2 size={18} className="animate-spin" /> {t('Submitting...', 'verify.submitting')}
              </div>
            )}

            {!verifying && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {showPhoto && (
                  <button
                    data-testid="verify-photo-btn"
                    onClick={handleSubmitPhoto}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px',
                      borderRadius: 14, border: `1.5px solid ${TEAL}`, background: '#f0faf9',
                      cursor: 'pointer', width: '100%',
                    }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: `${TEAL}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Camera size={22} color={TEAL} />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A2E' }}>{t('Upload Photo', 'verify.photoTitle')}</div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{t('Take or upload a photo as proof', 'verify.photoSub')}</div>
                    </div>
                  </button>
                )}
                {showLocation && (
                  <button
                    data-testid="verify-location-btn"
                    onClick={handleSubmitLocation}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px',
                      borderRadius: 14, border: `1.5px solid #4E9FFF`, background: '#EEF3FB',
                      cursor: 'pointer', width: '100%',
                    }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(78,159,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <MapPin size={22} color="#4E9FFF" />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A2E' }}>{t('Share Location', 'verify.locTitle')}</div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{t('Confirm you are at the venue', 'verify.locSub')}</div>
                    </div>
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Leave Event confirmation — copy auto-adapts to whether the user has
        already verified (and therefore earned stars to lose) or not. */}
    {showLeaveConfirm && (() => {
      const wasVerified = myRecord?.status === 'approved';
      return (
        <div
          data-testid="leave-event-modal"
          onClick={() => !leaving && setShowLeaveConfirm(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#FFFFFF', borderRadius: 12, padding: 24,
              maxWidth: 340, width: '100%',
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1F2937' }}>
              Leave this event?
            </h3>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 20, lineHeight: 1.5 }}>
              {wasVerified
                ? 'Are you sure you want to leave this event? Your verified attendance will be removed and your stars for this event will be deducted.'
                : 'Are you sure you want to cancel your RSVP? You can rejoin later if spots are still open.'}
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                data-testid="leave-event-cancel-btn"
                onClick={() => setShowLeaveConfirm(false)}
                disabled={leaving}
                style={{
                  flex: 1, padding: '12px', background: '#F9FAFB',
                  border: '1px solid #E5E7EB', borderRadius: 8,
                  fontWeight: 500, cursor: leaving ? 'not-allowed' : 'pointer',
                  fontSize: 13, color: '#1F2937', opacity: leaving ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                data-testid="leave-event-confirm-btn"
                onClick={handleLeaveEvent}
                disabled={leaving}
                style={{
                  flex: 1, padding: '12px', background: '#DC2626',
                  color: '#FFFFFF', border: 'none', borderRadius: 8,
                  fontWeight: 500, cursor: leaving ? 'not-allowed' : 'pointer',
                  fontSize: 13, opacity: leaving ? 0.7 : 1,
                }}
              >
                {leaving ? 'Leaving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      );
    })()}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#1A1A2E', letterSpacing: 1, marginBottom: 8, fontFamily: 'Poppins, sans-serif' }}>
        {label}
      </div>
      {children}
    </div>
  );
}
